import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Zap,
  Plus,
  Play,
  Trash2,
  Edit2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  History,
  FlaskConical,
} from "lucide-react";

const TABS = [
  { id: "workflows",  icon: Zap },
  { id: "executions", icon: History },
  { id: "test",       icon: FlaskConical },
];

const STATUS_STYLE = {
  success:    { cls: "bg-green-100 text-green-800", icon: CheckCircle },
  failed:     { cls: "bg-red-100 text-red-800",     icon: XCircle },
  skipped:    { cls: "bg-gray-100 text-gray-600",   icon: Clock },
  would_fire: { cls: "bg-blue-100 text-blue-800",   icon: Play },
};

const TRIGGER_COLORS = {
  employee_hired:         "bg-green-100 text-green-800",
  contract_expiring:      "bg-orange-100 text-orange-800",
  probation_ending:       "bg-yellow-100 text-yellow-800",
  birthday:               "bg-pink-100 text-pink-800",
  work_anniversary:       "bg-purple-100 text-purple-800",
  absence_approved:       "bg-red-100 text-red-800",
  performance_review_due: "bg-blue-100 text-blue-800",
  onboarding_overdue:     "bg-slate-100 text-slate-800",
};

// ── Workflows Tab ─────────────────────────────────────────────────────────────

function defaultAction() {
  return { action_type: "send_notification", action_config: { title: "", message: "" } };
}

const statusLabel = (status, t) => t(`workflowAutomationHub.status.${status}`, { defaultValue: status });

function ActionEditor({ action, index, onChange, onRemove, actionLabels }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(index === 0);
  const cfg = action.action_config || {};

  const updateCfg = (key, val) => {
    onChange({ ...action, action_config: { ...cfg, [key]: val } });
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center justify-center font-bold">{index + 1}</span>
          <span className="text-sm font-medium text-gray-700">{actionLabels[action.action_type] || action.action_type}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={e => { e.stopPropagation(); onRemove(); }} className="p-1 text-gray-400 hover:text-red-500">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3 border-t">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.actionType")}</label>
            <Select value={action.action_type} onValueChange={v => onChange({ ...action, action_type: v, action_config: {} })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(actionLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {(action.action_type === "send_notification" || action.action_type === "send_email_alert") && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.title")}</label>
                <Input className="h-8 text-sm" value={cfg.title || ""} onChange={e => updateCfg("title", e.target.value)} placeholder={t("workflowAutomationHub.actionEditor.titlePlaceholder")} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("workflowAutomationHub.actionEditor.message")} <span className="text-gray-400 font-normal">{t("workflowAutomationHub.actionEditor.variables")}</span>
                </label>
                <Textarea rows={2} className="text-sm" value={cfg.message || ""} onChange={e => updateCfg("message", e.target.value)} placeholder={t("workflowAutomationHub.actionEditor.messagePlaceholder")} />
              </div>
            </>
          )}

          {action.action_type === "assign_onboarding" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.onboardingTemplateId")}</label>
              <Input className="h-8 text-sm" type="number" value={cfg.template_id || ""} onChange={e => updateCfg("template_id", parseInt(e.target.value))} placeholder={t("workflowAutomationHub.actionEditor.idPlaceholder")} />
            </div>
          )}

          {action.action_type === "create_task" && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.taskTitle")}</label>
                <Input className="h-8 text-sm" value={cfg.title || ""} onChange={e => updateCfg("title", e.target.value)} placeholder={t("workflowAutomationHub.actionEditor.taskTitlePlaceholder")} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.dueDays")}</label>
                <Input className="h-8 text-sm" type="number" value={cfg.due_days || 7} onChange={e => updateCfg("due_days", parseInt(e.target.value))} />
              </div>
            </>
          )}

          {action.action_type === "log_event" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.actionEditor.logMessage")}</label>
              <Input className="h-8 text-sm" value={cfg.message || ""} onChange={e => updateCfg("message", e.target.value)} placeholder={t("workflowAutomationHub.actionEditor.logMessagePlaceholder")} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkflowsTab() {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [stats, setStats] = useState(null);
  const [triggerLabels, setTriggerLabels] = useState({});
  const [actionLabels, setActionLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", trigger_event: "employee_hired", trigger_days_offset: 0, is_active: true, actions: [defaultAction()] });

  const load = async () => {
    setLoading(true);
    try {
      const [wf, s, tl, al] = await Promise.all([
        apiClient.get("v1/hr-workflows"),
        apiClient.get("v1/hr-workflows/stats"),
        apiClient.get("v1/hr-workflows/meta/triggers"),
        apiClient.get("v1/hr-workflows/meta/actions"),
      ]);
      setWorkflows(wf.data);
      setStats(s.data);
      setTriggerLabels(tl.data);
      setActionLabels(al.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    const payload = { ...form, trigger_days_offset: parseInt(form.trigger_days_offset) || 0 };
    if (editing) {
      await apiClient.patch(`v1/hr-workflows/${editing.id}`, payload);
    } else {
      await apiClient.post("v1/hr-workflows", payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ name: "", description: "", trigger_event: "employee_hired", trigger_days_offset: 0, is_active: true, actions: [defaultAction()] });
    load();
  };

  const openEdit = (wf) => {
    setEditing(wf);
    setForm({
      name: wf.name,
      description: wf.description || "",
      trigger_event: wf.trigger_event,
      trigger_days_offset: wf.trigger_days_offset,
      is_active: wf.is_active,
      actions: wf.actions?.map(a => ({ action_type: a.action_type, action_config: a.action_config })) || [defaultAction()],
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("workflowAutomationHub.workflows.deleteConfirm"))) return;
    await apiClient.delete(`v1/hr-workflows/${id}`);
    load();
  };

  const updateAction = (index, updated) => {
    const actions = [...form.actions];
    actions[index] = updated;
    setForm(f => ({ ...f, actions }));
  };

  const removeAction = (index) => {
    setForm(f => ({ ...f, actions: f.actions.filter((_, i) => i !== index) }));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("workflowAutomationHub.common.loading")}</div>;

  const timeBased = ["contract_expiring", "probation_ending", "birthday"];

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("workflowAutomationHub.workflows.stats.active"), value: stats.active_workflows, color: "text-blue-600" },
            { label: t("workflowAutomationHub.workflows.stats.totalExecutions"), value: stats.total_executions, color: "text-purple-600" },
            { label: t("workflowAutomationHub.workflows.stats.successRate"), value: `${stats.success_rate}%`, color: "text-green-600" },
            { label: t("workflowAutomationHub.workflows.stats.firedToday"), value: stats.fired_today, color: "text-orange-600" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("workflowAutomationHub.workflows.count", { count: workflows.length })}</h3>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("workflowAutomationHub.workflows.new")}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-6 space-y-5">
          <h4 className="font-semibold text-gray-800">{editing ? t("workflowAutomationHub.workflows.editTitle") : t("workflowAutomationHub.workflows.newTitle")}</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.common.name")}</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t("workflowAutomationHub.workflows.namePlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.workflows.triggerEvent")}</label>
              <Select value={form.trigger_event} onValueChange={v => setForm(f => ({ ...f, trigger_event: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(triggerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {timeBased.includes(form.trigger_event) && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.workflows.daysBeforeEvent")}</label>
                <Input type="number" min="0" value={form.trigger_days_offset} onChange={e => setForm(f => ({ ...f, trigger_days_offset: e.target.value }))} />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.common.description")}</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t("workflowAutomationHub.common.optionalDescription")} />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              <label htmlFor="active" className="text-sm cursor-pointer">{t("workflowAutomationHub.workflows.activeWorkflow")}</label>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Actions ({form.actions.length})</label>
              <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, actions: [...f.actions, defaultAction()] }))}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("workflowAutomationHub.common.add")}
              </Button>
            </div>
            <div className="space-y-2">
              {form.actions.map((action, i) => (
                <ActionEditor
                  key={i}
                  action={action}
                  index={i}
                  actionLabels={actionLabels}
                  onChange={updated => updateAction(i, updated)}
                  onRemove={() => removeAction(i)}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSubmit}>{editing ? t("workflowAutomationHub.common.save") : t("workflowAutomationHub.common.create")}</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>{t("workflowAutomationHub.common.cancel")}</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {workflows.map((wf) => (
          <div key={wf.id} className={`bg-white rounded-xl border p-5 ${!wf.is_active ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TRIGGER_COLORS[wf.trigger_event] || "bg-gray-100 text-gray-700"}`}>
                    {triggerLabels[wf.trigger_event] || wf.trigger_event}
                  </span>
                  {wf.trigger_days_offset > 0 && (
                    <span className="text-xs text-gray-500">- {t("workflowAutomationHub.workflows.daysBefore", { count: wf.trigger_days_offset })}</span>
                  )}
                  {!wf.is_active && <Badge variant="outline" className="text-xs">{t("workflowAutomationHub.workflows.inactive")}</Badge>}
                </div>
                <h4 className="font-semibold text-gray-900">{wf.name}</h4>
                {wf.description && <p className="text-xs text-gray-500 mt-0.5">{wf.description}</p>}
              </div>
              <div className="flex items-center gap-1 ml-3">
                <Button variant="ghost" size="icon" onClick={() => openEdit(wf)}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(wf.id)}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {wf.actions?.map((a, i) => (
                <div key={i} className="flex items-center gap-1 bg-gray-50 rounded-lg px-2.5 py-1 text-xs text-gray-600">
                  <span className="w-4 h-4 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center justify-center font-bold">{i + 1}</span>
                  {a.action_type.replace(/_/g, " ")}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Executions Tab ────────────────────────────────────────────────────────────

function ExecutionsTab() {
  const { t, i18n } = useTranslation();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    apiClient.get("v1/hr-workflows/executions?limit=100").then(r => setExecutions(Array.isArray(r.data) ? r.data : [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t("workflowAutomationHub.common.loading")}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("workflowAutomationHub.executions.count", { count: executions.length })}</h3>
        <button onClick={() => apiClient.get("v1/hr-workflows/executions?limit=100").then(r => setExecutions(Array.isArray(r.data) ? r.data : []))} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> {t("workflowAutomationHub.common.refresh")}
        </button>
      </div>

      {executions.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Clock className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p>{t("workflowAutomationHub.executions.empty")}</p>
          <p className="text-xs mt-1">{t("workflowAutomationHub.executions.emptyHint")}</p>
        </div>
      )}

      <div className="space-y-2">
        {executions.map((ex) => {
          const s = STATUS_STYLE[ex.status] || STATUS_STYLE.success;
          const Icon = s.icon;
          const isExpanded = expanded === ex.id;
          return (
            <div key={ex.id} className="bg-white rounded-xl border overflow-hidden">
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpanded(isExpanded ? null : ex.id)}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${ex.status === "success" ? "text-green-500" : "text-red-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-sm truncate">
                    {ex.workflow?.name || t("workflowAutomationHub.executions.workflowFallback", { id: ex.workflow_id })}
                  </div>
                  <div className="text-xs text-gray-500">
                    {ex.employee ? `${ex.employee.first_name} ${ex.employee.last_name}` : t("workflowAutomationHub.executions.employeeFallback", { id: ex.employee_id })}
                    {" · "}{new Date(ex.triggered_at).toLocaleString(i18n.language)}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{statusLabel(ex.status, t)}</span>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>

              {isExpanded && ex.result?.actions && (
                <div className="border-t bg-gray-50 px-4 py-3 space-y-1.5">
                  {ex.result.actions.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {a.status === "ok"
                        ? <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        : <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      }
                      <span className="font-medium text-gray-600 capitalize">{a.action.replace(/_/g, " ")}</span>
                      <span className="text-gray-500">— {a.detail}</span>
                    </div>
                  ))}
                  {ex.error_message && (
                    <div className="text-xs text-red-500 mt-1">{ex.error_message}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Test / Manual Fire Tab ────────────────────────────────────────────────────

function TestTab() {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedWf, setSelectedWf] = useState("");
  const [selectedEmp, setSelectedEmp] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [evalResult, setEvalResult] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [dryRun, setDryRun] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("v1/hr-workflows"),
      apiClient.get("v1/employees/"),
    ]).then(([wf, emp]) => {
      setWorkflows(wf.data);
      setEmployees(emp.data.employees || emp.data || []);
    });
  }, []);

  const handleFire = async () => {
    if (!selectedWf || !selectedEmp) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await apiClient.post(`v1/hr-workflows/${selectedWf}/fire`, { employee_id: parseInt(selectedEmp) });
      setResult(r.data);
    } catch (e) {
      setResult({ status: "failed", error: e.message });
    } finally {
      setRunning(false);
    }
  };

  const handleEvaluate = async () => {
    setEvaluating(true);
    setEvalResult(null);
    try {
      const r = await apiClient.post("v1/hr-workflows/evaluate", { dry_run: dryRun });
      setEvalResult(r.data);
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Manual fire */}
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Play className="h-5 w-5 text-blue-500" />
          <h3 className="font-semibold text-gray-800">{t("workflowAutomationHub.test.manualTitle")}</h3>
        </div>
        <p className="text-sm text-gray-500">{t("workflowAutomationHub.test.manualDescription")}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.test.workflow")}</label>
            <Select value={selectedWf} onValueChange={setSelectedWf}>
              <SelectTrigger><SelectValue placeholder={t("workflowAutomationHub.test.chooseWorkflow")} /></SelectTrigger>
              <SelectContent>
                {workflows.map(wf => <SelectItem key={wf.id} value={String(wf.id)}>{wf.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("workflowAutomationHub.test.employee")}</label>
            <Select value={selectedEmp} onValueChange={setSelectedEmp}>
              <SelectTrigger><SelectValue placeholder={t("workflowAutomationHub.test.chooseEmployee")} /></SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={String(e.id)}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={handleFire} disabled={!selectedWf || !selectedEmp || running}>
          <Play className={`h-4 w-4 mr-1 ${running ? "animate-pulse" : ""}`} />
          {running ? t("workflowAutomationHub.test.running") : t("workflowAutomationHub.test.fire")}
        </Button>

        {result && (
          <div className={`rounded-lg p-4 text-sm ${result.status === "success" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2 font-medium mb-2">
              {result.status === "success"
                ? <CheckCircle className="h-4 w-4 text-green-500" />
                : <XCircle className="h-4 w-4 text-red-500" />
              }
              {result.status === "success" ? t("workflowAutomationHub.test.fireSuccess") : t("workflowAutomationHub.status.failed")}
            </div>
            {result.actions?.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-gray-600 mt-1">
                <span className={a.status === "ok" ? "text-green-500" : "text-red-500"}>•</span>
                <span className="font-medium capitalize">{a.action.replace(/_/g, " ")}</span>
                <span>— {a.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evaluate all */}
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw className="h-5 w-5 text-purple-500" />
          <h3 className="font-semibold text-gray-800">{t("workflowAutomationHub.test.dailyTitle")}</h3>
        </div>
        <p className="text-sm text-gray-500">{t("workflowAutomationHub.test.dailyDescription")}</p>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            {t("workflowAutomationHub.test.dryRun")}
          </label>
        </div>

        <Button variant={dryRun ? "outline" : "default"} onClick={handleEvaluate} disabled={evaluating}>
          <RefreshCw className={`h-4 w-4 mr-1 ${evaluating ? "animate-spin" : ""}`} />
          {evaluating ? t("workflowAutomationHub.test.evaluating") : dryRun ? t("workflowAutomationHub.test.simulate") : t("workflowAutomationHub.test.evaluate")}
        </Button>

        {evalResult && (
          <div className="rounded-lg p-4 bg-purple-50 border border-purple-200 space-y-3">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><div className="text-xl font-bold text-green-600">{evalResult.fired}</div><div className="text-xs text-gray-500">{t("workflowAutomationHub.test.fired")}</div></div>
              <div><div className="text-xl font-bold text-gray-400">{evalResult.skipped}</div><div className="text-xs text-gray-500">{t("workflowAutomationHub.test.skipped")}</div></div>
              <div><div className="text-xl font-bold text-red-500">{evalResult.errors}</div><div className="text-xs text-gray-500">{t("workflowAutomationHub.test.errors")}</div></div>
            </div>
            {evalResult.log?.length > 0 && (
              <div className="space-y-1 border-t pt-3">
                {evalResult.log.map((entry, i) => (
                  <div key={i} className="text-xs text-gray-600 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.status === "success" || entry.status === "would_fire" ? "bg-green-400" : "bg-red-400"}`} />
                    <span className="font-medium">{entry.workflow}</span>
                    <span className="text-gray-400">→</span>
                    <span>{entry.employee}</span>
                    {dryRun && <Badge variant="outline" className="text-xs py-0">{t("workflowAutomationHub.status.would_fire")}</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hub ────────────────────────────────────────────────────────────────────────

export default function WorkflowAutomationHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "workflows";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "workflows":  return <WorkflowsTab />;
      case "executions": return <ExecutionsTab />;
      case "test":       return <TestTab />;
      default:           return <WorkflowsTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-amber-50">
          <Zap className="h-6 w-6 text-amber-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("workflowAutomationHub.title")}</h1>
          <p className="text-sm text-gray-500">{t("workflowAutomationHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(`workflowAutomationHub.tabs.${tab.id}`)}
            </button>
          );
        })}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
